import { Select } from '@mantine/core'
import { PanelDetailsContext } from 'App/Map/PanelDetails/PanelDetails'
import type { TLngLat, TValuesTimeseriesPoints } from 'App/Map/types'
import { translate } from 'i18n/i18n'
import { useContext, useMemo } from 'react'
import { colors } from 'settings/colors'
import { hubConnectorSettings } from 'settings/hubConnectorSettings'
import { SharedSettings } from 'settings/SharedSettings'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TPointCategory } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { alphabeticalSort } from 'utils/alphabeticalSort'
import { dropRepeatedSelectValues } from 'utils/dropRepeatedSelectValues'
import { filterFieldAssetsByValueTypes } from 'utils/filterFieldAssetsByValueTypes'
import { getPointExternalVendorNames } from 'utils/getPointExternalVendorNames'
import type { TSelectItemWithExternalVendors } from 'utils/makeExternalVendorSelectProps'
import { makeExternalVendorSelectProps } from 'utils/makeExternalVendorSelectProps'
import { mantineSelectSearchFilterProp } from 'utils/mantineSelectSearchFilterProp'
import { setSelectedFieldAsset } from 'utils/setSelectedFieldAsset/setSelectedFieldAsset'
import { sortByKey } from 'utils/sortByKey'
import { useSelectClickToggle } from 'utils/useSelectClickToggle'

type SelectDataGroup = { group: string; items: (TSelectItemWithExternalVendors & { value: TLngLat })[] }

export type TDropdownSelectorPointProps = {
  valuesTimeseriesToFilterOn?: TValuesTimeseriesPoints[]
  pointCategory?: TPointCategory
}

export const DropdownSelectorPoint = ({
  valuesTimeseriesToFilterOn,
  pointCategory,
}: TDropdownSelectorPointProps) => {
  const { selectedPoint } = selectedFieldAssetsStore.useSelector((s) => ({
    selectedPoint: pointCategory ? s[pointCategory] : s.lngLat,
  }))

  const keyToSelectOnChange = pointCategory ?? 'lngLat'
  const { containerWidth } = useContext(PanelDetailsContext)
  const selectClickToggle = useSelectClickToggle()

  const { properties } = fieldAssetStore.useSelector((s) => ({
    properties: s?.properties ?? [],
  }))

  const data = useMemo<SelectDataGroup[]>(() => {
    const groups = Object.values(properties ?? {})
      .sort(sortByKey('propertyName'))
      .flatMap((p) => {
        if (!p.points) return []

        let filteredPoints = Object.values(p.points)

        if (valuesTimeseriesToFilterOn) {
          filteredPoints = filterFieldAssetsByValueTypes({
            fieldAssets: Object.values(p.points),
            valuesTimeseries: valuesTimeseriesToFilterOn,
          })
        }

        if (filteredPoints.length === 0) return []

        return [
          {
            group: p.propertyName,
            items: filteredPoints
              .sort((a, b) => alphabeticalSort(a.name, b.name))
              .map((point) => ({
                value: point.lngLat,
                label: point.name,
                externalVendorNames: getPointExternalVendorNames({
                  point,
                  valueTypes: valuesTimeseriesToFilterOn ?? point.valuesTimeseries,
                }),
              })),
          },
        ]
      })

    return dropRepeatedSelectValues(groups)
  }, [selectedPoint, properties])

  if (data.length === 0) return null

  const { selectProps, inputPaddingRight } = makeExternalVendorSelectProps({ data, value: selectedPoint })

  return (
    // can prevent the section from collapsing by passing stopPropagation
    <div onClick={(e) => e.stopPropagation()}>
      <Select
        {...selectClickToggle}
        css={{
          width: containerWidth < 600 ? 200 : Math.min(280, Math.round(containerWidth / 3)),
          border: `1px solid ${colors.grey500}`,
          color: colors.grey800,
          borderRadius: 3,
        }}
        clearable={false}
        data={data}
        {...selectProps}
        filter={mantineSelectSearchFilterProp}
        onChange={(newPoint) => {
          if (newPoint !== null) setSelectedFieldAsset({ [keyToSelectOnChange]: newPoint as TLngLat })
        }}
        placeholder={translate.phrases.banyanApp('Select a Station')}
        searchable
        size="xs"
        spellCheck="false"
        classNames={SharedSettings.MANTINE_SELECT_UNDERLINE_SEPARATOR_CLASSNAMES}
        styles={{
          ...SharedSettings.MANTINE_SELECT_RIGHT_ICON_CHANGER,
          ...SharedSettings.MANTINE_SELECT_UNDERLINE_SEPARATOR_STYLES,
          dropdown: { marginTop: -6, fontWeight: 'normal' },
          input: {
            ...SharedSettings.MANTINE_SELECT_RIGHT_ICON_CHANGER.input,
            paddingRight: inputPaddingRight,
          },
        }}
        value={selectedPoint}
        comboboxProps={{
          withinPortal: true,
          keepMounted: false,
          zIndex: hubConnectorSettings.zIndexForDropdownSelectorInStackemCharts,
        }}
      />
    </div>
  )
}
